import axios from 'axios';

import Attribution from '@web-solutions/module-attribution';

/**
 * Attaches the captured FB ad labels to the two user-API calls that carry them:
 *
 *  - `POST /v2/user`                 — user creation, the earliest point there is
 *  - `PUT /user/update-by-token/:id` — every quiz save, the main working path
 *
 * The backend stamps the FIRST snapshot it ever receives and never overwrites,
 * so re-sending on every call is deliberate — there is nothing to deduplicate.
 *
 * Done as an interceptor rather than at each call site: between them these two
 * requests are fired from ~20 pages and hooks (every personal-plan variant, the
 * paywall, the email popups, the anonymous-user hook), and a call that quietly
 * misses the labels stays invisible until the cohort report is already wrong.
 *
 * This is also the only channel that reaches the backend for EVERY payment
 * system on this brand: the TailoredPay purchase itself goes through /nmi/*
 * (which carries the labels too), but the user record is created here first.
 */
const UPDATE_BY_TOKEN_PATH = '/user/update-by-token/';
const CREATE_USER_PATH = '/v2/user';

/**
 * Capture at module-import time, not from a React render.
 *
 * The entry file imports this module as its second side effect, so the URL is
 * read before the store hydrates, before the first render and before any hook
 * can fire a request. `Attribution.init()` also captures, but it runs inside the
 * `Root` component body — late enough that an early `POST /v2/user` could race
 * it and go out with no labels.
 */
Attribution.captureAdAttribution();

let registered = false;

export function registerAdAttributionInterceptor(): void {
  // StrictMode renders the root twice — a second interceptor would be harmless
  // but pointless.
  if (registered) {
    return;
  }
  registered = true;

  axios.interceptors.request.use((config) => {
    try {
      const url = config.url || '';
      const method = config.method?.toLowerCase();

      const isCreateUser = method === 'post' && url.includes(CREATE_USER_PATH);
      const isUpdateByToken = method === 'put' && url.includes(UPDATE_BY_TOKEN_PATH);

      if (!isCreateUser && !isUpdateByToken) {
        return config;
      }

      const attribution = Attribution.getAdAttribution();

      // Organic visit, or a snapshot the backend would discard anyway — no
      // `attribution` key at all, not an empty object.
      if (!attribution) {
        return config;
      }

      // `POST /v2/user` is sometimes called with no body at all (the anonymous
      // user hook), so there is nothing to spread — start from an empty object.
      const data = config.data ?? (isCreateUser ? {} : undefined);

      if (!data || typeof data !== 'object' || data instanceof FormData) {
        return config;
      }

      config.data = { ...data, attribution };
    } catch (_) {
      // Attribution must never break creating the user or saving the questionnaire.
    }

    return config;
  });
}

registerAdAttributionInterceptor();
